/**
 * Lightweight usage analytics client.
 * - Stable anonymous UID stored in localStorage (survives across pages).
 * - On feature "open": POST /api/analytics/event { action: 'open' }
 * - On feature "close" (unmount / tab hide / unload): sendBeacon with duration_ms.
 * - Silent — never blocks UI, never surfaces errors to the user.
 */
import axios from "axios";
import { useEffect, useRef } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const UID_KEY = "prn_analytics_uid";

function anonUid() {
  try {
    let uid = localStorage.getItem(UID_KEY);
    if (!uid) {
      uid = (crypto.randomUUID?.() || `u_${Date.now()}_${Math.random().toString(36).slice(2)}`);
      localStorage.setItem(UID_KEY, uid);
    }
    return uid;
  } catch {
    // sandboxed iframe / private mode — fall back to in-memory session UID
    if (!window.__prnAnonUidMem) {
      window.__prnAnonUidMem = (crypto.randomUUID?.() || `u_${Date.now()}_${Math.random().toString(36).slice(2)}`);
    }
    return window.__prnAnonUidMem;
  }
}

async function postEvent(body) {
  if (!BACKEND) return;
  try {
    await axios.post(`${BACKEND}/api/analytics/event`, body, {
      headers: { "Content-Type": "application/json" },
    });
  } catch { /* silent */ }
}

function beaconEvent(body) {
  if (!BACKEND || !navigator.sendBeacon) {
    postEvent(body);
    return;
  }
  try {
    const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
    navigator.sendBeacon(`${BACKEND}/api/analytics/event`, blob);
  } catch { /* silent */ }
}

/**
 * useFeatureTimer(feature)
 * Logs "open" on mount, sends "close" with duration on unmount / tab hide / beforeunload.
 * Feature must be one of the backend whitelist:
 *   welcome | simulation | references | code_scribe | admin
 */
export function useFeatureTimer(feature) {
  const startedAt = useRef(null);
  const closedRef = useRef(false);

  useEffect(() => {
    if (!feature) return;
    const uid = anonUid();
    startedAt.current = Date.now();
    closedRef.current = false;

    postEvent({ feature, action: "open", session_uid: uid });

    const closeOnce = () => {
      if (closedRef.current) return;
      closedRef.current = true;
      const duration_ms = Date.now() - startedAt.current;
      // Always use beacon in the close path — POST via axios can be aborted on route change / unload.
      beaconEvent({ feature, action: "close", session_uid: uid, duration_ms });
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") closeOnce();
    };
    window.addEventListener("beforeunload", closeOnce);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("beforeunload", closeOnce);
      document.removeEventListener("visibilitychange", onVisibility);
      closeOnce();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feature]);}

/**
 * Fetch admin analytics summary. Requires admin token.
 */
export async function getAnalyticsSummary(token) {
  const res = await axios.get(`${BACKEND}/api/analytics/summary`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
}
